import React, { useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, CircularProgress,
  Avatar, List, ListItem, ListItemAvatar, ListItemText, Chip, Divider,
  IconButton, Tooltip
} from '@mui/material';
import {
  People as PeopleIcon, Person as TeacherIcon, School as ClassIcon,
  MenuBook as SubjectIcon, HowToReg as EnrolIcon, TrendingUp as TrendIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { reportsAPI } from '../services/api';
import { exportChartSVG } from '../utils/export';
import { useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#1565c0', '#e53935', '#2e7d32', '#f57c00', '#6a1b9a', '#00838f'];

const StatCard = ({ title, value, sub, icon, color = 'primary.main' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h4" fontWeight={700}>{value ?? '—'}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isClassTeacher = user?.role === 'class_teacher';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const barChartRef = useRef(null);
  const pieChartRef = useRef(null);

  useEffect(() => {
    reportsAPI.getDashboard(isClassTeacher && user?.teacher_id ? { teacher_id: user.teacher_id } : {})
      .then(res => { if (res.data.success) setData(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={48} /></Box>;

  const genderData = [
    { name: 'Male', value: parseInt(data?.students?.male) || 0 },
    { name: 'Female', value: parseInt(data?.students?.female) || 0 }
  ];
  const totalStudents = genderData[0].value + genderData[1].value;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Dashboard</Typography>
      {data?.active_year && (
        <Chip label={`${data.active_year.year_label} — ${data.active_year.term}`}
          color="primary" variant="outlined" sx={{ mb: 2 }} />
      )}

      <Grid container spacing={3}>
        {/* Stat Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Students" value={data?.students?.total} sub={`${data?.students?.active} active`} icon={<PeopleIcon />} color="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Teachers" value={data?.teachers?.total} sub={`${data?.teachers?.active} active`} icon={<TeacherIcon />} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Classes" value={data?.classes?.total} sub="Active classes" icon={<ClassIcon />} color="#f57c00" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Subjects" value={data?.subjects?.total} sub="Registered subjects" icon={<SubjectIcon />} color="#6a1b9a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Enrolled" value={data?.enrolments?.total_enrolled} sub="This term" icon={<EnrolIcon />} color="#00838f" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Male Enrolled" value={data?.enrolments?.enrolled_male} sub="This term" icon={<TrendIcon />} color="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Female Enrolled" value={data?.enrolments?.enrolled_female} sub="This term" icon={<TrendIcon />} color="#e53935" />
        </Grid>

        {/* Class distribution bar chart */}
        {data?.class_distribution?.length > 0 && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>Enrolment by Class</Typography>
                  <Tooltip title="Export chart as SVG">
                    <IconButton size="small" onClick={() => exportChartSVG(barChartRef, 'enrolment_by_class')}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <div ref={barChartRef}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.class_distribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <ChartTooltip />
                    <Bar dataKey="count" fill="#1565c0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Gender pie chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Student Gender</Typography>
                <Tooltip title="Export chart as SVG">
                  <IconButton size="small" onClick={() => exportChartSVG(pieChartRef, 'student_gender')}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <div ref={pieChartRef}>
              {totalStudents === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 260, color: 'text.disabled' }}>
                  <PeopleIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2">No student data yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={genderData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={true}>
                      {genderData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <ChartTooltip formatter={(val, name) => [val, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent enrolments */}
        {data?.recent_enrolments?.length > 0 && (
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Recent Enrolments</Typography>
                <List dense>
                  {data.recent_enrolments.map((e, i) => (
                    <React.Fragment key={i}>
                      <ListItem disablePadding sx={{ py: 0.5 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36, fontSize: 14 }}>
                            {e.first_name?.[0]}{e.last_name?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${e.first_name} ${e.last_name}`}
                          secondary={`${e.admission_number} · ${e.class_name}`}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: 12 }}
                        />
                      </ListItem>
                      {i < data.recent_enrolments.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
